import { paysByLink } from "../payment-mode";

/**
 * Ambassador program rules (owner, 2026-10-09). One place, read by the API,
 * the web dashboard, admin and the app, so every surface shows the same
 * dirhams. Earnings are derived from `reservations.commission`, never kept
 * as a running balance: an admin correction (or the likely-no-show tag that
 * zeroes commission) flows through until the payout freezes the amount.
 */

/** The ambassador's cut of OUR commission. */
export const AMBASSADOR_SHARE = 0.5;
/** A link click counts for this long; the last ambassador clicked wins. */
export const REFERRAL_WINDOW_DAYS = 30;
/** Activities settle this many days after the activity date. */
export const ACTIVITY_SETTLE_DAYS = 3;
/** Payouts go out by bank transfer on this day of every month. */
export const PAYOUT_DAY_OF_MONTH = 15;

export const AMBASSADOR_KINDS = ["activity", "restaurant", "spa"] as const;
export type AmbassadorKind = (typeof AMBASSADOR_KINDS)[number];

export const AMBASSADOR_HANDLE_RE = /^[a-z0-9_]{3,32}$/;

/** Lowercase, no leading @. Null when it is not a valid handle. */
export function normalizeHandle(raw: string | null | undefined): string | null {
  const value = raw?.trim().replace(/^@/, "").toLowerCase();
  return value && AMBASSADOR_HANDLE_RE.test(value) ? value : null;
}

/** Whole dirhams, never negative. */
export function ambassadorShareMad(commissionMad: number | null | undefined): number {
  if (!commissionMad || !Number.isFinite(commissionMad) || commissionMad <= 0) return 0;
  return Math.round(commissionMad * AMBASSADOR_SHARE);
}

/**
 * Which payout rule a booking follows, or null when it is outside the
 * program. `template` must be the RESOLVED template (inferCategoryTemplate),
 * not the raw nullable column. Bookable rows carry no segment, so the
 * category slug is checked alongside it (a bookable spa is template
 * "experience" in category "spas").
 */
export function ambassadorKindFor(input: {
  template: string | null;
  segment: string | null;
  categorySlug: string | null;
  activitySlug: string | null;
  isTransfer: boolean;
}): AmbassadorKind | null {
  // Camps live in activities but are stays, paid by an emailed link.
  if (paysByLink(input.activitySlug)) return null;
  if (input.isTransfer || input.template === "transfer" || input.categorySlug === "transport") return null;
  if (input.template === "stay" || input.segment === "stays" || input.categorySlug === "stays") return null;
  if (
    input.template === "restaurant" ||
    input.segment === "restaurants" ||
    input.categorySlug === "restaurants"
  ) {
    return "restaurant";
  }
  if (input.segment === "spas" || input.categorySlug === "spas") return "spa";
  return "activity";
}

/** YYYY-MM-DD in Morocco's clock; the 15th is a Moroccan 15th. */
export function todayInCasablanca(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Casablanca",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

const pad = (n: number) => String(n).padStart(2, "0");

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True for a plain YYYY-MM-DD; the date maths below assumes nothing else. */
export function isYmd(value: string | null | undefined): value is string {
  return typeof value === "string" && YMD_RE.test(value);
}

export function addDays(date: string, days: number): string {
  if (!isYmd(date)) throw new Error(`addDays: expected YYYY-MM-DD, got ${date}`);
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** The coming 15th; today counts when today is the 15th. */
export function nextPayoutDate(today: string): string {
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  if (d <= PAYOUT_DAY_OF_MONTH) return `${y}-${pad(m)}-${pad(PAYOUT_DAY_OF_MONTH)}`;
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return `${ny}-${pad(nm)}-${pad(PAYOUT_DAY_OF_MONTH)}`;
}

/** Would this booking be paid in the payout dated `payoutDate`? */
export function isDueBy(input: {
  kind: AmbassadorKind;
  status: string;
  activityDate: string | null;
  payoutDate: string;
}): boolean {
  if (input.status !== "completed" && input.status !== "paid") return false;
  if (!isYmd(input.activityDate)) return false;
  if (input.kind === "activity") {
    return addDays(input.activityDate, ACTIVITY_SETTLE_DAYS) <= input.payoutDate;
  }
  return input.activityDate < input.payoutDate;
}

export type EarningState = "waiting" | "due" | "paid" | "void";

export function earningState(input: {
  status: string;
  kind: AmbassadorKind;
  /** Null or malformed reads as "not settled yet", never as due. */
  activityDate: string | null;
  shareMad: number;
  /** Status of the payout this booking was frozen into, if any. */
  payoutStatus: "pending" | "paid" | null;
  today: string;
}): EarningState {
  if (input.payoutStatus === "paid") return "paid";
  if (input.payoutStatus === "pending") return "due";
  if (input.status === "cancelled" || input.shareMad <= 0) return "void";
  return isDueBy({ ...input, payoutDate: nextPayoutDate(input.today) }) ? "due" : "waiting";
}
